import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import { useSessionStore, resetSessionExpiredFlag } from '../../stores/sessionStore';
import { resetAuthInterceptorState } from '../../api/client';
import { prepareSignInAgain } from '../../auth/returnAfterSignIn';

/**
 * Full-screen modal shown when the auth token has expired and silent refresh
 * failed. Clears the dead sign-in and redirects the user to the login page.
 *
 * The tab's connection and facility are kept and the current page is
 * remembered, so signing in again returns the user to where they were
 * (see auth/returnAfterSignIn.ts). A different person signing in on the tab
 * gets the connection picker instead (validateConnectionOwner).
 *
 * Prevents multiple simultaneous redirects via the sessionExpiredTriggered flag
 * in sessionStore.
 */
export default function SessionExpiredDialog() {
  const isSessionExpired = useSessionStore((s) => s.isSessionExpired);

  const handleLogin = () => {
    // Drop the dead sign-in and per-tab state; keep the connection, the
    // facility and the page to come back to.
    const { pathname, search, hash } = window.location;
    prepareSignInAgain(`${pathname}${search}${hash}`);

    // Reset all auth/session state so fresh login starts clean
    resetSessionExpiredFlag();
    resetAuthInterceptorState();
    useSessionStore.getState().resetSession();

    // Redirect to the app root - AuthGuard will handle re-authentication
    // (Keycloak signinRedirect in prod, DebugTokenSetter in dev)
    window.location.href = '/';
  };

  if (!isSessionExpired) return null;

  return (
    <Dialog
      open
      maxWidth="xs"
      fullWidth
      slotProps={{ backdrop: { sx: { backdropFilter: 'blur(4px)' } } }}
      // Prevent closing by clicking outside or pressing Escape
      onClose={(_e, reason) => {
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pb: 1 }}>
        <i
          className="bi bi-shield-lock"
          style={{ fontSize: 'var(--font-size-large)', color: 'var(--color-negative)' }}
        />
        <span>Session Expired</span>
      </DialogTitle>

      <DialogContent>
        <p style={{ fontSize: 'var(--font-size-base)', color: 'var(--text-secondary)', margin: '0 0 12px' }}>
          Your session has expired. Please log in again to continue.
        </p>
        <p style={{ fontSize: 'var(--font-size-small)', color: 'var(--text-secondary)', margin: 0 }}>
          Any unsaved changes may have been lost.
        </p>
      </DialogContent>

      <DialogActions>
        <Button
          variant="contained"
          onClick={handleLogin}
          sx={{ textTransform: 'none', fontSize: 'var(--font-size-base)' }}
        >
          Log In Again
        </Button>
      </DialogActions>
    </Dialog>
  );
}
